import { io, Socket } from 'socket.io-client';
import { Idea } from './api';
import { offlineService } from './offlineService';

export type SyncStatus = 'connected' | 'disconnected' | 'syncing' | 'error';

interface SyncEvent {
  type: 'idea:created' | 'idea:updated' | 'idea:deleted' | 'project:created';
  data: any;
  timestamp: string;
}

class SyncService {
  private socket: Socket | null = null;
  private status: SyncStatus = 'disconnected';
  private listeners: Map<string, Set<Function>> = new Map();
  private offlineQueue: SyncEvent[] = [];

  connect(token: string) {
    let serverUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
    // Remove /api suffix if present
    if (serverUrl.endsWith('/api')) {
      serverUrl = serverUrl.slice(0, -4);
    }
    
    this.socket = io(serverUrl, {
      auth: { token },
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: Infinity
    });

    this.setupEventHandlers();
  }

  private setupEventHandlers() {
    if (!this.socket) return;

    this.socket.on('connect', () => {
      console.log('WebSocket connected');
      this.updateStatus('connected');
      this.processOfflineQueue();
    });

    this.socket.on('disconnect', () => {
      console.log('WebSocket disconnected');
      this.updateStatus('disconnected');
    });

    this.socket.on('sync:connected', (data) => {
      console.log('Sync connected:', data);
    });

    this.socket.on('sync:error', (error) => {
      console.error('Sync error:', error);
      this.updateStatus('error');
    });

    // Handle real-time updates
    this.socket.on('idea:created', (idea: Idea) => {
      this.emit('ideaCreated', idea);
    });

    this.socket.on('idea:updated', (idea: Idea) => {
      this.emit('ideaUpdated', idea);
    });

    this.socket.on('idea:deleted', (data: { id: string }) => {
      this.emit('ideaDeleted', data.id);
    });

    this.socket.on('project:created', (project: any) => {
      this.emit('projectCreated', project);
    });

    this.socket.on('sync:queue_processed', (data) => {
      console.log(`Processed ${data.processed} offline changes`);
    });
  }

  private updateStatus(status: SyncStatus) {
    this.status = status;
    this.emit('statusChanged', status);
  }

  private async processOfflineQueue() {
    if (!this.socket || this.offlineQueue.length === 0) return;

    this.updateStatus('syncing');
    
    // Get pending changes from offline service
    const pendingIdeas = await offlineService.getPendingIdeas();
    
    const queue = [
      ...this.offlineQueue,
      ...pendingIdeas.map(idea => ({
        type: 'idea:create' as const,
        data: idea,
        timestamp: new Date().toISOString()
      }))
    ];

    if (queue.length > 0) {
      this.socket.emit('sync:offline_queue', queue);
      this.offlineQueue = [];
      
      // Clear offline storage after successful sync
      await offlineService.clearPendingIdeas();
    }
    
    this.updateStatus('connected');
  }

  // Emit events to local listeners
  private emit(event: string, data: any) {
    const listeners = this.listeners.get(event);
    if (listeners) {
      listeners.forEach(listener => listener(data));
    }
  }

  // Subscribe to sync events
  on(event: string, callback: Function) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)!.add(callback);

    // Return unsubscribe function
    return () => {
      const listeners = this.listeners.get(event);
      if (listeners) {
        listeners.delete(callback);
      }
    };
  }

  // Send events to server
  emitToServer(event: string, data: any) {
    if (this.socket && this.status === 'connected') {
      this.socket.emit(event, data);
    } else {
      // Queue for later if offline
      this.offlineQueue.push({
        type: event as any,
        data,
        timestamp: new Date().toISOString()
      });
    }
  }

  getStatus(): SyncStatus {
    return this.status;
  }

  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
  }
}

export const syncService = new SyncService();